import React from 'react'
import { RadioGroup } from '@chakra-ui/react'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface RadioGroupFormControlComponentProps {
  valueAsNumber?: boolean
  valueAsBoolean?: boolean
  valueAsArray?: boolean
  zeroAsNull?: boolean
  children: React.ReactNode
}

// Base component that handles radio group logic
export const RadioGroupFormControlBase = React.forwardRef<
  HTMLDivElement,
  RadioGroupFormControlComponentProps & FormFieldProps<string | number | boolean | (string | number | boolean | null)[] | null, HTMLDivElement>
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    children,
    valueAsNumber,
    valueAsBoolean,
    valueAsArray,
    zeroAsNull
  } = props

  const radioGroupValue = Array.isArray(value)
    ? value
        .map((v) =>
          valueAsBoolean
            ? v?.toString().toLowerCase() === 'true'
            : valueAsNumber
              ? (zeroAsNull && !v) ? 0 : Number(v ?? 0)
              : v,
        )
        .toString()
    : valueAsBoolean || valueAsNumber
      ? (zeroAsNull && !value) ? '0' : value?.toString() ?? ''
      : value?.toString() ?? ''

  const onRadioGroupValueChange = (value: string) => {
    const typedValue = valueAsNumber
      ? (zeroAsNull && value == '0') ? null : Number(value)
      : valueAsBoolean
        ? value.toLowerCase() === 'true'
        : value
    onChange(valueAsArray ? [typedValue] : typedValue)
    onBlur()
  }

  return (
    <RadioGroup
      ref={ref}
      name={name}
      value={radioGroupValue}
      onChange={onRadioGroupValueChange}
      onBlur={onBlur}
      isDisabled={isDisabled}
      alignSelf="start"
    >
      {children}
    </RadioGroup>
  )
})

;(RadioGroupFormControlBase as any).displayName = 'RadioGroupFormControlBase'

// Export the wrapped component
export const RadioGroupFormControl = withFormControl<RadioGroupFormControlComponentProps, string | number | boolean | (string | number | boolean | null)[] | null, HTMLDivElement>(RadioGroupFormControlBase)